import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, MessageSquare, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { contactAPI } from '../utils/api';

// Mirror the FAB's subject taxonomy so admins see consistent categories
// regardless of which entry point the user used.
const SUBJECT_OPTIONS = [
  'General Enquiry',
  'Account / Login Issue',
  'Listing Issue',
  'Payment / Subscription',
  'Dealer Plan Enquiry',
  'Report Abuse',
  'Feedback / Suggestion',
  'Other',
];

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: 'General Enquiry', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill in name, email, and message');
      return;
    }
    setSubmitting(true);
    try {
      await contactAPI.submit({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        subject: form.subject.trim() || 'General Enquiry',
        message: form.message.trim(),
      });
      setSubmitted(true);
      toast.success("Message received — we'll respond within 24 hours.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Could not send your message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(160deg, #fff5f0 0%, #f0edff 50%, #fff5f0 100%)' }}>
      {/* Header bar — matches Privacy/Terms pages */}
      <div className="py-5 sm:py-7 relative overflow-hidden" style={{ background: '#1800ad' }}>
        <div className="absolute inset-0 opacity-10" style={{ background: 'radial-gradient(circle at 20% 50%, #fff 0%, transparent 40%)' }} />
        <div className="max-w-4xl mx-auto px-4 sm:px-8 relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-xs sm:text-sm font-medium mb-2 sm:mb-3 transition-colors" data-testid="back-link">
            <ArrowLeft size={14} /> Back to Home
          </Link>
          <div className="flex items-center gap-2.5">
            <MessageSquare size={22} className="text-white sm:w-6 sm:h-6" />
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white" data-testid="contact-title">Contact Us</h1>
          </div>
          <p className="text-white/70 text-[11px] sm:text-xs mt-1.5">We're here to help. Reach out anytime.</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-6 sm:py-10">
        <div className="grid md:grid-cols-5 gap-6">
          {/* Left — quick contact channels */}
          <aside className="md:col-span-2 space-y-4">
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#1800ad]/10">
              <h2 className="text-sm font-bold text-[#1800ad] mb-3 uppercase tracking-wide">Get in touch</h2>

              <a href="mailto:help@genzox.in" className="flex items-start gap-3 py-2.5 group" data-testid="contact-email">
                <div className="w-9 h-9 rounded-full bg-[#1800ad]/10 text-[#1800ad] flex items-center justify-center flex-shrink-0 group-hover:bg-[#1800ad] group-hover:text-white transition-colors">
                  <Mail size={16} />
                </div>
                <div>
                  <p className="text-[11px] text-gray-500 uppercase tracking-wide">Email</p>
                  <p className="text-sm font-semibold text-[#0A0B10]">help@genzox.in</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">We respond within 24 hours.</p>
                </div>
              </a>
            </div>

            <div className="bg-gradient-to-br from-[#1800ad] to-[#ff8500] rounded-2xl p-5 text-white">
              <h3 className="text-sm font-bold mb-1">Need help with an order?</h3>
              <p className="text-xs text-white/85 leading-relaxed">
                Mention your <strong>listing ID</strong> or <strong>transaction ID</strong> in the message — it speeds things up.
              </p>
            </div>
          </aside>

          {/* Right — contact form */}
          <div className="md:col-span-3">
            {submitted ? (
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-green-200 text-center" data-testid="contact-success">
                <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 size={28} />
                </div>
                <h2 className="text-lg font-bold text-[#0A0B10] mb-2">Thanks for reaching out!</h2>
                <p className="text-sm text-gray-600 mb-5">
                  We received your message and will respond to <strong>{form.email}</strong> within 24 hours.
                </p>
                <button
                  onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', subject: 'General Enquiry', message: '' }); }}
                  className="text-sm font-semibold text-[#1800ad] hover:underline"
                  data-testid="contact-send-another"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-[#1800ad]/10 space-y-4">
                <h2 className="text-base font-bold text-[#0A0B10] mb-1">Send us a message</h2>
                <p className="text-xs text-gray-500 mb-3">Fields marked <span className="text-red-500">*</span> are required.</p>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                    <input
                      type="text" value={form.name} onChange={update('name')} required
                      placeholder="Your name"
                      data-testid="contact-input-name"
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1800ad] focus:ring-1 focus:ring-[#1800ad]/20"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                    <input
                      type="email" value={form.email} onChange={update('email')} required
                      placeholder="you@example.com"
                      data-testid="contact-input-email"
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1800ad] focus:ring-1 focus:ring-[#1800ad]/20"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel" value={form.phone} onChange={update('phone')}
                      placeholder="+91 99999 99999"
                      data-testid="contact-input-phone"
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1800ad] focus:ring-1 focus:ring-[#1800ad]/20"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Subject</label>
                    <select
                      value={form.subject}
                      onChange={update('subject')}
                      data-testid="contact-input-subject"
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1800ad] focus:ring-1 focus:ring-[#1800ad]/20 bg-white"
                    >
                      {SUBJECT_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Message <span className="text-red-500">*</span></label>
                  <textarea
                    rows={5} value={form.message} onChange={update('message')} required
                    placeholder="Tell us how we can help..."
                    data-testid="contact-input-message"
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1800ad] focus:ring-1 focus:ring-[#1800ad]/20 resize-none"
                  />
                </div>

                <button
                  type="submit" disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="w-full bg-[#1800ad] hover:bg-[#1500a0] disabled:opacity-60 text-white rounded-xl px-5 py-3 font-bold transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Send size={16} /> {submitting ? 'Sending...' : 'Send Message'}
                </button>

                <p className="text-[11px] text-gray-500 text-center">
                  By submitting, you agree to our <Link to="/privacy" className="text-[#1800ad] hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-[#1800ad] hover:underline">Terms</Link>.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
